
import React, { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

const Anvandingsfall = () => {
  useEffect(() => {
    // Smooth scroll function for anchor links
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const id = target.getAttribute('href')?.substring(1);
        if (id) {
          const element = document.getElementById(id);
          if (element) {
            window.scrollTo({
              top: element.offsetTop - 80, // Account for fixed header
              behavior: 'smooth'
            });
          }
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  // Define the use cases specific to Snabba upphandlingar
  const useCases = [
    "Kommunala tjänsteupphandlingar",
    "IT-systemupphandlingar",
    "Byggentreprenader",
    "Vårdupphandlingar",
    "Konsulttjänster",
    "Transportupphandlingar",
    "Livsmedelsupphandlingar",
    "Utbildningstjänster",
    "Försörjningssektorupphandlingar",
    "Ramavtalsupphandlingar",
    "Försvarsupphandlingar",
    "Innovationspartnerskap",
    "Tekniska konsulttjänster",
    "Kontorsmaterial och inventarier",
    "Avfallshantering",
    "Säkerhetstjänster",
    "Energieffektiviseringar",
    "Fastighetsunderhåll",
    "Digitala lösningar",
    "Hälso- och sjukvårdstjänster",
    "Anläggningsarbeten",
    "Konferenstjänster",
    "Finansiella tjänster",
    "Fordon och transportmedel",
  ];

  // Create three identical copies to ensure infinite looping effect works smoothly
  const duplicatedUseCases = [...useCases, ...useCases, ...useCases];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-20 w-full overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 text-center mb-20">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-bold text-center mb-4 text-darkgray">
            100+ Användingsfall
          </h1>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-montserrat font-medium text-center mb-8 text-darkgray">
            från 20+ Branscher
          </h2>
        </div>

        {/* Use Cases Container */}
        <ScrollArea className="w-full overflow-x-hidden">
          <div className="py-8">
            {/* Row 1 - Right to Left */}
            <div className="flex gap-4 mb-8 animate-marquee-right whitespace-nowrap px-4" style={{ animationDuration: '120s' }}>
              {duplicatedUseCases.map((useCase, idx) => (
                <div key={`row1-${idx}`} className="flex-none rounded-full bg-gray-200/80 py-2 px-6 flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap font-opensans">{useCase}</span>
                </div>
              ))}
            </div>
            
            {/* Row 2 - Left to Right */}
            <div className="flex gap-4 mb-8 animate-marquee-left whitespace-nowrap px-4" style={{ animationDuration: '150s' }}>
              {duplicatedUseCases.map((useCase, idx) => (
                <div key={`row2-${idx}`} className="flex-none rounded-full bg-gray-200/80 py-2 px-6 flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap font-opensans">{useCase}</span>
                </div>
              ))}
            </div>
            
            {/* Row 3 - Right to Left */}
            <div className="flex gap-4 mb-8 animate-marquee-right whitespace-nowrap px-4" style={{ animationDuration: '135s' }}>
              {duplicatedUseCases.map((useCase, idx) => (
                <div key={`row3-${idx}`} className="flex-none rounded-full bg-gray-200/80 py-2 px-6 flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap font-opensans">{useCase}</span>
                </div>
              ))}
            </div>
            
            {/* Row 4 - Left to Right */}
            <div className="flex gap-4 mb-8 animate-marquee-left whitespace-nowrap px-4" style={{ animationDuration: '180s' }}>
              {duplicatedUseCases.map((useCase, idx) => (
                <div key={`row4-${idx}`} className="flex-none rounded-full bg-gray-200/80 py-2 px-6 flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap font-opensans">{useCase}</span>
                </div>
              ))}
            </div>
            
            {/* Row 5 - Right to Left */}
            <div className="flex gap-4 mb-8 animate-marquee-right whitespace-nowrap px-4" style={{ animationDuration: '165s' }}>
              {duplicatedUseCases.map((useCase, idx) => (
                <div key={`row5-${idx}`} className="flex-none rounded-full bg-gray-200/80 py-2 px-6 flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap font-opensans">{useCase}</span>
                </div>
              ))}
            </div>
            
            {/* Row 6 - Left to Right */}
            <div className="flex gap-4 animate-marquee-left whitespace-nowrap px-4" style={{ animationDuration: '145s' }}>
              {duplicatedUseCases.map((useCase, idx) => (
                <div key={`row6-${idx}`} className="flex-none rounded-full bg-gray-200/80 py-2 px-6 flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap font-opensans">{useCase}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>
        
        {/* Call to Action Button */}
        <div className="container mx-auto px-4 sm:px-6 text-center mt-16 mb-8">
          <Button 
            size="lg" 
            className="font-montserrat font-bold text-base sm:text-lg px-8 py-6 h-auto"
          >
            Börja gratis <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Anvandingsfall;
